import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '../context/UserContext';
import { schemaFor, destinationSummary } from '../utils/deliveryConnectors';
import SearchableSelect from '../components/SearchableSelect';
import { buildStatusGroupMap, buildProgramOptionGroups, closedProgramIdSet, buildOwningProgramOptionGroups } from '../utils/statusGroups';

const API = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Style constants (matches LeadQA.js / LeadUpload.js conventions)
// ---------------------------------------------------------------------------
const thStyle = {
  padding: '7px 12px', textAlign: 'left', borderBottom: '1px solid #aed6f1',
  fontWeight: '600', color: '#1a6da8', fontSize: '12px', userSelect: 'none',
  whiteSpace: 'nowrap', background: '#eaf4fb',
};
const tdStyle = {
  padding: '7px 12px', borderBottom: '1px solid #eaf2fb', fontSize: '12px', color: '#333',
};
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '4px', display: 'block', fontWeight: '600' };
const inputStyle = {
  padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};
const btnStyle = (color, disabled) => ({
  padding: '6px 14px', background: disabled ? '#bdc3c7' : color,
  color: 'white', border: 'none', borderRadius: '4px',
  cursor: disabled ? 'default' : 'pointer', fontWeight: '600', fontSize: '12px',
});
const sectionStyle = {
  background: 'white', border: '1px solid #e0e6ed', borderRadius: '8px',
  padding: '18px 20px', marginBottom: '20px',
};
const sectionTitleStyle = { margin: '0 0 12px 0', fontSize: '16px', color: '#2c3e50' };

const IS_STAGING = (API || '').includes('staging');

function StatusBadge({ status }) {
  const colors = {
    Delivered: { bg: '#eafaf1', color: '#1e8449', border: '#a9dfbf' },
    Failed:    { bg: '#fdedec', color: '#c0392b', border: '#f5b7b1' },
    Simulated: { bg: '#eaf2fb', color: '#1a6da8', border: '#aed6f1' },
  }[status] || { bg: '#f4f6f7', color: '#566573', border: '#d5dbdb' };
  return (
    <span style={{
      background: colors.bg, color: colors.color, border: `1px solid ${colors.border}`,
      borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: '600',
    }}>{status || '—'}</span>
  );
}

// Drag-and-drop file picker (pattern from SupplierPortal.js)
function FileDrop({ file, onFile, hint }) {
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
      onClick={() => fileRef.current?.click()}
      style={{
        border: `2px dashed ${dragging ? '#3498db' : file ? '#27ae60' : '#ccc'}`,
        borderRadius: '8px', padding: '14px 18px', textAlign: 'center', cursor: 'pointer',
        background: dragging ? '#eaf4fb' : file ? '#eafaf1' : '#fafafa',
        transition: 'border-color 0.15s, background 0.15s', userSelect: 'none', minWidth: '280px',
      }}
    >
      <div style={{ fontSize: '13px', fontWeight: file ? '600' : '400', color: file ? '#1e8449' : dragging ? '#1a6da8' : '#555' }}>
        {file ? `✅ ${file.name}` : dragging ? '📂 Drop to use this file' : `📁 ${hint} — drop here or click to browse`}
      </div>
      {file && (
        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
          {(file.size / 1024).toFixed(1)} KB · <span onClick={(e) => { e.stopPropagation(); onFile(null); }}
            style={{ color: '#e74c3c', cursor: 'pointer', textDecoration: 'underline' }}>Remove</span>
        </div>
      )}
      <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }}
             onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
}

// Find a column in a feedback-check row's raw `data` to match leads back by
// — case/space/underscore-insensitive. Two very different kinds of file end
// up here: a results CSV NeTRA itself generated (release-csv's download)
// carries the NeTRA Lead ID, since it started from a leads export — but a
// feedback/results file coming back FROM the client's own system generally
// doesn't (the client never saw our internal ID), so email is the reliable
// shared key there instead. Prefer Lead ID when both are present (a
// precise 1:1 key); this is a best-effort auto-detect either way — the UI
// falls back to letting the PM pick the column (and which kind it is) by
// hand when neither can be found.
function detectLeadIdColumn(dataObj) {
  const keys = Object.keys(dataObj || {});
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const exact = keys.find((k) => ['leadid', 'netraleadid'].includes(norm(k)));
  if (exact) return exact;
  return keys.find((k) => norm(k).includes('leadid')) || null;
}

function detectEmailColumn(dataObj) {
  const keys = Object.keys(dataObj || {});
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const exact = keys.find((k) => norm(k) === 'email');
  if (exact) return exact;
  return keys.find((k) => norm(k).includes('email')) || null;
}

// When the uploaded file has no external_id (feedback-check couldn't poll a
// connector for it — see backend), the client's own status/verdict for each
// lead has to come from a column already in that file, not from anything
// NeTRA computed. Best-effort auto-detect; the UI falls back to a manual
// picker when this doesn't find anything.
const STATUS_COLUMN_HINTS = ['status', 'result', 'verdict', 'disposition', 'outcome'];
function detectStatusColumn(dataObj) {
  const keys = Object.keys(dataObj || {});
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const exact = keys.find((k) => STATUS_COLUMN_HINTS.includes(norm(k)));
  if (exact) return exact;
  return keys.find((k) => STATUS_COLUMN_HINTS.some((h) => norm(k).includes(h))) || null;
}

// A client's results file sometimes carries a separate free-text reason
// column (richer than the coarse accept/reject status, e.g. "Bad phone
// number" vs. just "Rejected") — auto-detect it the same best-effort way as
// the status column. Optional: when none is found/picked, the status value
// itself (whatever the PM bucketed into "rejected") is used as the reason
// instead — see reasonFor() below.
const REASON_COLUMN_HINTS = ['reason', 'rejectreason', 'rejectionreason', 'qaflag', 'qaflags', 'comment', 'comments', 'notes', 'feedback'];
function detectReasonColumn(dataObj) {
  const keys = Object.keys(dataObj || {});
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const exact = keys.find((k) => REASON_COLUMN_HINTS.includes(norm(k)));
  if (exact) return exact;
  return keys.find((k) => REASON_COLUMN_HINTS.some((h) => norm(k).includes(h))) || null;
}

const MATCH_MODE_LABELS = { lead_id: 'NeTRA Lead ID', email: 'Email address' };

// Build and download a CSV from an array of objects (uniform keys assumed)
function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headers = Object.keys(rows[0]);
  const lines = [headers.map(esc).join(',')];
  rows.forEach((r) => lines.push(headers.map((h) => esc(r[h])).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Main page — PM operations: deliver CSVs, check feedback.
// Connections/destinations are configured on the Delivery Setup page (admin).
// ---------------------------------------------------------------------------
export default function LeadDelivery() {
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [destinations, setDestinations] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programStatuses, setProgramStatuses] = useState([]);
  const [components, setComponents] = useState([]);
  const [goals, setGoals] = useState([]);
  const [error, setError] = useState('');

  // Closed programs nest into a collapsible section on every Program/
  // Destination picker below instead of mixing into the flat list — see
  // src/utils/statusGroups.js. The "Match against leads in ___" Component/Goal
  // pickers further down stay plain native <select>s (not converted here) —
  // they're guarded by disabled={!matchProgramId}/{!matchComponentId}, which
  // SearchableSelect has no prop for, and each is already scoped to a single
  // chosen program/component so an active/closed split there is low-value.
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const programOptionGroups = buildProgramOptionGroups(programs, programStatusGroupMap);
  const closedProgramIds = closedProgramIdSet(programs, programStatusGroupMap);

  // release section state
  const [releaseDestId, setReleaseDestId] = useState('');
  const [releaseFile, setReleaseFile] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [releaseResult, setReleaseResult] = useState(null);
  // Delivery runs as a background job (see backend CLAUDE.md's "Delivery
  // jobs" note) — release() gets a job_id back immediately and this page
  // polls GET /release-jobs/{id} until it's done, instead of holding one
  // HTTP request open for the whole per-row push loop (which was long
  // enough on a big batch for a reverse proxy to drop the connection and
  // show a false "Network Error" even though delivery succeeded).
  const [releaseJob, setReleaseJob] = useState(null);   // {processed, total, delivered, failed} while running
  const releasePollRef = useRef(null);
  // Recent-deliveries recovery panel: lets a PM reattach to a still-running
  // job after leaving/reloading the page (the backend keeps working
  // regardless — only the frontend's polling was lost), or re-view an old
  // completed job's results without re-uploading the CSV.
  const [releaseHistory, setReleaseHistory] = useState([]);
  const [releaseHistoryLoading, setReleaseHistoryLoading] = useState(false);
  const [preflight, setPreflight] = useState(null);       // validate-csv response
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [dryRun, setDryRun] = useState(IS_STAGING);       // staging defaults to simulate-only

  // feedback section state
  const [fbDestId, setFbDestId] = useState('');
  const [fbFile, setFbFile] = useState(null);
  const [fbChecking, setFbChecking] = useState(false);
  const [fbResult, setFbResult] = useState(null);

  // "Update NeTRA lead status from feedback" state — which column in the
  // re-uploaded results file holds the match key (matchMode says whether
  // that column is a NeTRA Lead ID or an email address), which column (if
  // any) holds the client's own status/verdict when no connector was polled
  // for this file, and which of the resulting status values should be
  // treated as "the client accepted this lead" vs. "the client rejected this
  // lead." One status can only mean one or the other (or neither) — a plain
  // {status: 'accepted' | 'rejected'} map, cycled by clicking its pill,
  // rather than two independent checkbox sets that could disagree.
  const [matchMode, setMatchMode] = useState('');     // 'lead_id' | 'email'
  const [matchColumn, setMatchColumn] = useState('');
  const [statusColumn, setStatusColumn] = useState('');
  // Which column (if any) holds the client's own free-text rejection
  // reason/QA flag — auto-detected, optional. When unset, the status value
  // itself (whichever one the PM bucketed as "rejected") is used as the
  // reason instead — see reasonFor() below. Stored onto Lead.qa_flags for
  // any lead marked Rejected, so a supplier can see WHY, not just THAT.
  const [reasonColumn, setReasonColumn] = useState('');
  const [statusDecisions, setStatusDecisions] = useState(() => ({}));
  const [applyingStatus, setApplyingStatus] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // Scope to search for matching leads when matching by email — seeded from
  // the Check Feedback destination's own program/component/goal, but
  // independently overridable. The destination's component/goal is about
  // delivery *routing* (which segment this push went to), not a promise that
  // every lead in the client's returned results file falls under that same
  // narrow slice — a client's results commonly span several components under
  // one program. Defaulting to the destination's scope keeps the common
  // single-component case a no-op, while letting a PM widen to the whole
  // program (clear the component/goal selects) when it doesn't.
  const [matchProgramId, setMatchProgramId] = useState('');
  const [matchComponentId, setMatchComponentId] = useState('');
  const [matchGoalId, setMatchGoalId] = useState('');

  useEffect(() => {
    axios.get(`${API}/lead-delivery/destinations`).then((r) => setDestinations(r.data)).catch(() => {});
    axios.get(`${API}/programs/`).then((r) => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/components/`).then((r) => setComponents(r.data)).catch(() => {});
    axios.get(`${API}/goals/`).then((r) => setGoals(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then((r) => setProgramStatuses(r.data)).catch(() => {});
  }, []);

  const apiErr = (err) => setError(err.response?.data?.detail || String(err));

  // Preflight: runs automatically whenever both a destination and a file are
  // chosen — checks the CSV against the mapping WITHOUT pushing anything.
  const runPreflight = (destId, file) => {
    setPreflight(null);
    setReleaseResult(null);
    if (!destId || !file) return;
    setPreflightLoading(true);
    setError('');
    const fd = new FormData();
    fd.append('destination_id', destId);
    fd.append('file', file);
    axios.post(`${API}/lead-delivery/validate-csv`, fd)
      .then((r) => setPreflight(r.data))
      .catch(apiErr)
      .finally(() => setPreflightLoading(false));
  };

  const stopReleasePolling = () => {
    if (releasePollRef.current) { clearInterval(releasePollRef.current); releasePollRef.current = null; }
  };
  useEffect(() => () => stopReleasePolling(), []);   // clear the interval if the page is left mid-run

  // Explicitly (re)attach to a job's progress — used both by "resume
  // watching" in the history panel and by auto-reattach below. Safe to call
  // even if a poll is already running for this same job (just restarts the
  // interval); release() itself calls stopReleasePolling() first so a fresh
  // delivery is never watching a stale job's id.
  const watchReleaseJob = (jobId, seed) => {
    stopReleasePolling();
    setReleasing(true);
    setReleaseResult(null);
    setReleaseJob(seed);
    releasePollRef.current = setInterval(() => pollReleaseJob(jobId), 2000);
    pollReleaseJob(jobId);   // don't wait a full 2s for the first update
  };

  const loadReleaseHistory = (destId, { autoReattach } = {}) => {
    if (!destId) { setReleaseHistory([]); return; }
    setReleaseHistoryLoading(true);
    axios.get(`${API}/lead-delivery/release-jobs`, { params: { destination_id: destId } })
      .then((r) => {
        setReleaseHistory(r.data);
        // Reattach only when nothing is actively being watched yet (don't
        // steal focus from a delivery just kicked off in this same session)
        // and only the most recent job for this destination — an older one
        // stuck running behind it would be unusual and not what "resume
        // where I left off" should jump to.
        const latest = r.data[0];
        if (autoReattach && latest && !releasePollRef.current &&
            (latest.status === 'pending' || latest.status === 'running')) {
          watchReleaseJob(latest.id, latest);
        }
      })
      .catch(() => setReleaseHistory([]))
      .finally(() => setReleaseHistoryLoading(false));
  };

  const onReleaseDestChange = (destId) => {
    setReleaseDestId(destId);
    runPreflight(destId, releaseFile);
    loadReleaseHistory(destId, { autoReattach: true });
  };

  // Loads a past job's full results (including the per-row detail that the
  // lightweight history list doesn't carry) into releaseResult — same
  // rendering path a just-completed live delivery uses, so the results
  // table and "Download Results CSV" button both just work unchanged.
  const viewJobResults = (jobId) => {
    setError('');
    axios.get(`${API}/lead-delivery/release-jobs/${jobId}`)
      .then((r) => {
        const job = r.data;
        if (job.status !== 'completed') { setError('That job has no results yet.'); return; }
        stopReleasePolling();
        setReleaseJob(null);
        setReleaseResult({
          total: job.total, delivered: job.delivered, failed: job.failed,
          dry_run: job.dry_run, results: job.results, source: job.source,
        });
      })
      .catch(apiErr);
  };

  const pollReleaseJob = (jobId) => {
    axios.get(`${API}/lead-delivery/release-jobs/${jobId}`)
      .then((r) => {
        const job = r.data;
        if (job.status === 'completed') {
          stopReleasePolling();
          setReleaseJob(null);
          setReleaseResult({
            total: job.total, delivered: job.delivered, failed: job.failed,
            dry_run: job.dry_run, results: job.results, source: job.source,
          });
          setReleasing(false);
          loadReleaseHistory(releaseDestId);
        } else if (job.status === 'failed') {
          stopReleasePolling();
          setReleaseJob(null);
          setError(job.error || 'Delivery job failed.');
          setReleasing(false);
          loadReleaseHistory(releaseDestId);
        } else {
          // pending or running — keep polling, update the progress line
          setReleaseJob(job);
        }
      })
      .catch(() => { /* a transient poll failure isn't fatal — the interval just tries again */ });
  };

  const release = () => {
    if (!dryRun && IS_STAGING &&
        !window.confirm('This will REALLY send the leads to the client system from staging. Continue?')) {
      return;
    }
    setReleasing(true);
    setReleaseResult(null);
    setError('');
    const fd = new FormData();
    fd.append('destination_id', releaseDestId);
    fd.append('file', releaseFile);
    fd.append('dry_run', dryRun);
    axios.post(`${API}/lead-delivery/release-csv`, fd)
      .then((r) => {
        const { job_id, total, dry_run: jobDryRun } = r.data;
        watchReleaseJob(job_id, { status: 'pending', processed: 0, total, delivered: 0, failed: 0, dry_run: jobDryRun });
        loadReleaseHistory(releaseDestId);
      })
      .catch((err) => { apiErr(err); setReleasing(false); });
  };

  const checkFeedback = () => {
    setFbChecking(true);
    setFbResult(null);
    setApplyResult(null);
    setStatusDecisions({});
    setStatusColumn('');
    setReasonColumn('');
    setError('');
    const fd = new FormData();
    fd.append('destination_id', fbDestId);
    fd.append('file', fbFile);
    const checkedDest = destinations.find((d) => String(d.id) === String(fbDestId));
    setMatchProgramId(checkedDest?.program_id || '');
    setMatchComponentId(checkedDest?.component_id || '');
    setMatchGoalId(checkedDest?.goal_id || '');
    axios.post(`${API}/lead-delivery/feedback-check`, fd)
      .then((r) => {
        setFbResult(r.data);
        const firstRow = r.data.results?.[0]?.data;
        setReasonColumn(detectReasonColumn(firstRow) || '');
        if (r.data.polled_connector) {
          // NeTRA-generated results file — real Lead IDs may be present, and
          // the connector already supplied a live feedback_status per row.
          const leadIdCol = detectLeadIdColumn(firstRow);
          const emailCol = detectEmailColumn(firstRow);
          if (leadIdCol) { setMatchMode('lead_id'); setMatchColumn(leadIdCol); }
          else if (emailCol) { setMatchMode('email'); setMatchColumn(emailCol); }
          else { setMatchMode(''); setMatchColumn(''); }
        } else {
          // A file straight from the client's own system — no NeTRA Lead ID
          // to look for (skip that detector entirely to avoid false-
          // positive matching on some unrelated "their own lead id" column),
          // go straight to email; and since no connector was polled, the
          // client's verdict has to come from a column already in the file.
          const emailCol = detectEmailColumn(firstRow);
          setMatchMode(emailCol ? 'email' : '');
          setMatchColumn(emailCol || '');
          setStatusColumn(detectStatusColumn(firstRow) || '');
        }
      })
      .catch(apiErr)
      .finally(() => setFbChecking(false));
  };

  // Cycles a status pill through unset -> means Client Accepted -> means
  // Rejected -> unset. A status can only ever mean one or the other.
  const cycleStatusDecision = (status) => {
    setStatusDecisions((prev) => {
      const next = { ...prev };
      const current = next[status];
      if (current === 'accepted') next[status] = 'rejected';
      else if (current === 'rejected') delete next[status];
      else next[status] = 'accepted';
      return next;
    });
  };

  // Whether this file's feedback was actually polled live from a connector
  // (a NeTRA-generated results CSV, with a real external_id) vs. just read
  // back as-is (a file straight from the client's own system, with no
  // external_id — see backend feedback_check()).
  const feedbackFromFile = !!fbResult && fbResult.polled_connector === false;

  // The status to check a row against — the connector's own live answer
  // when we polled one, otherwise whatever verdict the client's file
  // already carries in the chosen status column.
  const statusFor = (r) => (feedbackFromFile ? r.data?.[statusColumn] : r.feedback_status);
  // Whether a row counts at all — the connector-polled path still needs
  // r.found (we asked the connector and got an answer back); a file we
  // never polled a connector for has no such concept, so any row counts.
  const isUsableRow = (r) => (feedbackFromFile ? true : r.found);

  // Values a given decision ('accepted' | 'rejected') would apply to: the
  // row counts (see isUsableRow), its status was assigned that decision,
  // and the row actually has a value in the chosen match column. Email
  // values are lowercased/trimmed client-side too (the backend does the
  // same) so the count shown here matches what the backend will actually
  // match on.
  const readyToMatch = !!matchColumn && (!feedbackFromFile || !!statusColumn);
  const matchesForDecision = (decision) => (
    fbResult && readyToMatch
      ? Array.from(new Set(
          fbResult.results
            .filter((r) => isUsableRow(r) && statusDecisions[statusFor(r)] === decision && r.data?.[matchColumn])
            .map((r) => String(r.data[matchColumn]).trim())
            .filter(Boolean)
            .map((v) => (matchMode === 'email' ? v.toLowerCase() : v))
        ))
      : []
  );
  const clientAcceptedMatches = matchesForDecision('accepted');
  const clientRejectedMatches = matchesForDecision('rejected');

  // The client's own stated reason for a row — a dedicated reason column
  // when one's set/found, otherwise the status value itself (whatever the
  // PM bucketed as "rejected"), which for Marketo is already the specific
  // progression_status text ("Not Delivered - ...") rather than a generic
  // pass/fail. Falls back to null (no reason to store) when neither exists.
  const reasonFor = (r) => {
    if (reasonColumn && r.data?.[reasonColumn]) return String(r.data[reasonColumn]).trim();
    const status = statusFor(r);
    return status ? String(status).trim() : null;
  };

  // Builds { matchKey: reason } for every row currently decided "rejected",
  // keyed the same way applyBulkStatus's own matches array is (lead_id
  // as-is, email lowercased) so the backend can look a lead's reason up by
  // the same key it matched the lead by.
  const rejectedReasons = () => {
    if (!fbResult || !readyToMatch) return {};
    const out = {};
    fbResult.results
      .filter((r) => isUsableRow(r) && statusDecisions[statusFor(r)] === 'rejected' && r.data?.[matchColumn])
      .forEach((r) => {
        let key = String(r.data[matchColumn]).trim();
        if (!key) return;
        if (matchMode === 'email') key = key.toLowerCase();
        const reason = reasonFor(r);
        if (reason) out[key] = reason;
      });
    return out;
  };

  const applyBulkStatus = (matches, status) => {
    if (!matches.length) return;
    setApplyingStatus(true);
    setApplyResult(null);
    setError('');
    const body = { status };
    if (matchMode === 'email') {
      body.emails = matches;
      body.program_id = matchProgramId ? Number(matchProgramId) : undefined;
      body.component_id = matchComponentId ? Number(matchComponentId) : undefined;
      body.goal_id = matchGoalId ? Number(matchGoalId) : undefined;
    } else {
      body.lead_ids = matches;
    }
    if (status === 'Rejected') {
      const reasons = rejectedReasons();
      if (Object.keys(reasons).length) body.reasons = reasons;
    }
    axios.post(`${API}/leads/bulk-status-update`, body)
      .then((r) => setApplyResult(r.data))
      .catch(apiErr)
      .finally(() => setApplyingStatus(false));
  };

  const downloadReleaseResults = () => {
    downloadCsv(`delivery-results-${new Date().toISOString().slice(0, 10)}.csv`,
      releaseResult.results.map((r) => ({
        ...(releaseResult.source === 'wizard'
          ? { netra_lead_id: r.netra_lead_id || '', lead_id: r.lead_id }
          : r.data),
        external_id: r.external_id || '', sync_status: r.sync_status || '', delivery_note: r.note || '',
        delivery_status: r.status, delivery_error: r.error || '',
      })));
  };

  const downloadFeedbackResults = () => {
    downloadCsv(`feedback-results-${new Date().toISOString().slice(0, 10)}.csv`,
      fbResult.results.map((r) => ({
        ...r.data, feedback_status: r.feedback_status || '',
        feedback_found: r.found ? 'yes' : 'no',
        feedback_detail: r.detail ? JSON.stringify(r.detail) : '',
      })));
  };

  const programName = (id) => programs.find((p) => p.id === id)?.program_name || `program #${id}`;
  const scopeLabel = (d) => {
    const parts = [programName(d.program_id)];
    if (d.component_id) parts.push(components.find((c) => c.id === d.component_id)?.component_name || `component #${d.component_id}`);
    if (d.goal_id) parts.push(goals.find((g) => g.id === d.goal_id)?.goal_name || `goal #${d.goal_id}`);
    return `${parts.join(' › ')} → ${d.connection_name}`;
  };
  const destLabel = (d) => {
    const summary = destinationSummary(d);
    return d.name
      ? `${d.name}${summary !== '—' ? ` (${summary})` : ''}`
      : scopeLabel(d);
  };
  const activeDests = destinations.filter((d) => d.active);
  // Closed programs nest into a collapsible section on the Destination
  // pickers below too — DeliveryDestination.program_id is direct, no lookup
  // needed. Custom destLabel means this uses the generic owning-program
  // splitter rather than buildProgramOptionGroups (which assumes a plain
  // program_name label).
  const destOptionGroups = buildOwningProgramOptionGroups(
    activeDests, (d) => d.program_id, closedProgramIds,
    (d) => ({ value: String(d.id), label: destLabel(d) }),
  );

  const fbDest = destinations.find((d) => String(d.id) === String(fbDestId));
  const fbFeedbackSupported = !fbDest || schemaFor(fbDest.connector_type).feedbackSupported;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ fontSize: '22px', color: '#2c3e50', marginBottom: '4px' }}>Client Leads Delivery</h1>
      <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: 0 }}>
        Upload a CSV against a configured destination, download the results (incl. assigned
        external IDs), and re-upload them later to check feedback.
        {isAdmin && <> Connections and destinations are managed in <Link to="/lead-delivery-setup">Delivery Setup</Link>.</>}
      </p>
      {error && (
        <div style={{ background: '#fdedec', border: '1px solid #f5b7b1', color: '#c0392b',
                      borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* ── 3. Deliver leads (CSV) ─────────────────────────────────────── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>1 · Deliver Leads (CSV upload)</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '320px' }}>
            <label style={labelStyle}>Destination</label>
            <SearchableSelect value={releaseDestId} onChange={onReleaseDestChange}
              options={destOptionGroups.options} closedOptions={destOptionGroups.closedOptions}
              placeholder="— select destination —" />
          </div>
          <div>
            <label style={labelStyle}>Leads CSV (checked against the mapping before anything is sent)</label>
            <FileDrop file={releaseFile} hint="Leads CSV"
                      onFile={(f) => { setReleaseFile(f); runPreflight(releaseDestId, f); }} />
          </div>
          <button style={btnStyle(dryRun ? '#1a6da8' : '#27ae60', !preflight?.ready || releasing)}
                  disabled={!preflight?.ready || releasing} onClick={release}>
            {releasing ? (dryRun ? 'Simulating…' : 'Delivering…')
                       : (dryRun ? '🧪 Simulate Delivery (nothing is sent)' : '🚀 Deliver Leads')}
          </button>
        </div>

        {releaseJob && (
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
            {releaseJob.status === 'pending'
              ? 'Starting…'
              : `Processing row ${releaseJob.processed} of ${releaseJob.total}` +
                (releaseJob.dry_run ? '' : ` — ${releaseJob.delivered} delivered, ${releaseJob.failed} failed`) +
                '.'}
            <div style={{ marginTop: '4px', height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden', maxWidth: '360px' }}>
              <div style={{
                height: '100%', background: dryRun ? '#1a6da8' : '#27ae60', borderRadius: '3px',
                width: `${releaseJob.total ? Math.round((releaseJob.processed / releaseJob.total) * 100) : 0}%`,
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        )}

        <div style={{
          marginTop: '10px', padding: '8px 12px', borderRadius: '6px', fontSize: '12px',
          background: dryRun ? '#eaf2fb' : '#fdf2e9', border: `1px solid ${dryRun ? '#aed6f1' : '#f5cba7'}`,
        }}>
          <label style={{ cursor: 'pointer', color: dryRun ? '#1a6da8' : '#ba4a00', fontWeight: '600' }}>
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
                   style={{ marginRight: '6px' }} />
            Test mode — run everything (login, mapping, payloads) but do NOT send to the client system
          </label>
          {dryRun
            ? <span style={{ color: '#555', marginLeft: '8px' }}>Uncheck to force a real upload{IS_STAGING ? ' from staging' : ''}.</span>
            : <span style={{ color: '#ba4a00', marginLeft: '8px' }}>⚠ Live mode — rows WILL be sent to the client system.</span>}
        </div>

        {preflightLoading && (
          <div style={{ marginTop: '12px', color: '#7f8c8d', fontSize: '13px' }}>Checking file against mapping…</div>
        )}
        {preflight && (
          <div style={{
            marginTop: '12px', borderRadius: '6px', padding: '12px 14px', fontSize: '13px',
            background: preflight.ready ? '#eafaf1' : '#fdedec',
            border: `1px solid ${preflight.ready ? '#a9dfbf' : '#f5b7b1'}`,
          }}>
            {preflight.ready ? (
              <div style={{ color: '#1e8449' }}>
                <strong>✓ File looks good.</strong> {preflight.row_count} rows; every destination field has a CSV column.
              </div>
            ) : (
              <div style={{ color: '#c0392b' }}>
                <strong>✗ Fix these before delivering ({preflight.row_count} rows read):</strong>
                <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  {preflight.problems.map((p, i) => <li key={i} style={{ marginBottom: '4px' }}>{p}</li>)}
                </ul>
                <div style={{ marginTop: '6px', color: '#555' }}>
                  Fix the file and re-choose it, or edit the destination's mapping, then it re-checks automatically.
                </div>
              </div>
            )}

            {preflight.pairs?.length > 0 && (
              <table style={{ borderCollapse: 'collapse', marginTop: '10px', background: 'white', minWidth: '60%' }}>
                <thead><tr>
                  <th style={thStyle}>Destination Field</th>
                  <th style={thStyle}>CSV Column</th>
                  <th style={thStyle}>Match</th>
                </tr></thead>
                <tbody>
                  {preflight.pairs.map((p, i) => {
                    const color = p.status === 'missing' ? '#c0392b'
                                : p.status === 'unmapped' ? '#95a5a6'
                                : p.status === 'auto' ? '#1a6da8' : '#1e8449';
                    const blanks = p.csv_column && preflight.blank_counts?.[p.csv_column];
                    return (
                      <tr key={i}>
                        <td style={{ ...tdStyle, color }}>
                          {p.destination_field || <em>— none (column ignored)</em>}
                        </td>
                        <td style={{ ...tdStyle, color }}>
                          {p.csv_column
                            ? <>{p.csv_column}{blanks ? <span style={{ color: '#b7950b' }}> · {blanks} blank</span> : null}</>
                            : <em>— not found (expected a header like "{p.wanted}")</em>}
                        </td>
                        <td style={{ ...tdStyle, color, whiteSpace: 'nowrap' }}>
                          {p.status === 'matched' && '✓ mapped'}
                          {p.status === 'auto' && '⇄ auto-matched'}
                          {p.status === 'missing' && '✗ missing'}
                          {p.status === 'unmapped' && 'ignored'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {releaseResult && (
          <div style={{ marginTop: '14px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
              {releaseResult.dry_run ? (
                <>
                  <StatusBadge status="Simulated" /> {releaseResult.total}
                  <span style={{ color: '#1a6da8', fontSize: '12px', fontWeight: '600' }}>
                    Test mode — nothing was sent. Review the payloads below, then uncheck test mode to deliver for real.
                  </span>
                </>
              ) : (
                <>
                  <StatusBadge status="Delivered" /> {releaseResult.delivered}
                  <StatusBadge status="Failed" /> {releaseResult.failed}
                  <span style={{ color: '#7f8c8d', fontSize: '12px' }}>of {releaseResult.total} rows</span>
                  <button style={btnStyle('#8e44ad')} onClick={downloadReleaseResults}>
                    ⬇ Download Results CSV (needed for feedback check)
                  </button>
                </>
              )}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={thStyle}>{releaseResult.source === 'wizard' ? 'Lead' : 'CSV Row'}</th>
                <th style={thStyle}>Status</th>
                {releaseResult.dry_run
                  ? <th style={thStyle}>Payload that would be sent</th>
                  : <><th style={thStyle}>External ID</th><th style={thStyle}>Sync Status</th><th style={thStyle}>Error</th></>}
              </tr></thead>
              <tbody>
                {releaseResult.results.map((r) => (
                  <tr key={releaseResult.source === 'wizard' ? r.lead_id : r.row}>
                    <td style={tdStyle}>{releaseResult.source === 'wizard' ? (r.netra_lead_id || r.lead_id) : r.row}</td>
                    <td style={tdStyle}><StatusBadge status={r.status} /></td>
                    {releaseResult.dry_run ? (
                      <td style={{ ...tdStyle, fontSize: '11px', fontFamily: 'monospace', color: '#444' }}>
                        {r.program_name && <div style={{ color: '#1e8449', fontWeight: 600 }}>→ Marketo program: {r.program_name}</div>}
                        {r.error && <div style={{ color: '#c0392b', fontWeight: 600 }}>{r.error}</div>}
                        {Object.entries(r.payload || {}).map(([k, v]) => `${k}=${v}`).join('  ')}
                      </td>
                    ) : (
                      <>
                        <td style={tdStyle}>{r.external_id || '—'}</td>
                        <td style={{ ...tdStyle, fontSize: '11px', color: '#555' }}>
                          {r.sync_status || '—'}{r.program_name ? ` · ${r.program_name}` : ''}
                          {r.note && <div style={{ color: '#b9770e', marginTop: '2px' }}>{r.note}</div>}
                        </td>
                        <td style={{ ...tdStyle, color: '#c0392b' }}>{r.error || ''}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {releaseDestId && (releaseHistoryLoading || releaseHistory.length > 0) && (
          <div style={{ marginTop: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
              Recent Deliveries for this destination
            </div>
            {releaseHistoryLoading ? (
              <div style={{ color: '#7f8c8d', fontSize: '12px' }}>Loading…</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={thStyle}>When</th><th style={thStyle}>Source</th><th style={thStyle}>Mode</th><th style={thStyle}>Status</th>
                  <th style={thStyle}>Rows</th><th style={thStyle}></th>
                </tr></thead>
                <tbody>
                  {releaseHistory.map((j) => (
                    <tr key={j.id}>
                      <td style={tdStyle}>{new Date(`${j.created_at}Z`).toLocaleString()}</td>
                      <td style={{ ...tdStyle, color: j.source === 'wizard' ? '#8e44ad' : '#555' }}>
                        {j.source === 'wizard' ? 'Wizard' : 'CSV'}
                      </td>
                      <td style={tdStyle}>{j.dry_run ? 'Test' : 'Live'}</td>
                      <td style={tdStyle}>
                        {(j.status === 'pending' || j.status === 'running') ? (
                          <span style={{ color: '#1a6da8' }}>
                            {j.status === 'pending' ? 'Starting…' : `Running (${j.processed}/${j.total})`}
                          </span>
                        ) : j.status === 'failed' ? (
                          <span style={{ color: '#c0392b' }} title={j.error || ''}>Failed{j.error ? ` — ${j.error}` : ''}</span>
                        ) : (
                          <StatusBadge status={j.dry_run ? 'Simulated' : 'Delivered'} />
                        )}
                      </td>
                      <td style={tdStyle}>
                        {j.dry_run ? j.total : <>{j.delivered} delivered{j.failed ? `, ${j.failed} failed` : ''} of {j.total}</>}
                      </td>
                      <td style={tdStyle}>
                        {(j.status === 'pending' || j.status === 'running') ? (
                          <button style={{ ...btnStyle('#1a6da8'), padding: '3px 10px', fontSize: '11px' }}
                                  onClick={() => watchReleaseJob(j.id, j)}>
                            ▶ Watch
                          </button>
                        ) : j.status === 'completed' ? (
                          <button style={{ ...btnStyle('#8e44ad'), padding: '3px 10px', fontSize: '11px' }}
                                  onClick={() => viewJobResults(j.id)}>
                            View Results
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── 4. Check feedback ──────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>2 · Check Feedback (re-upload results CSV)</h2>
        <p style={{ color: '#7f8c8d', fontSize: '12px', marginTop: 0 }}>
          Feedback from the receiving system can take time. Upload a results CSV — either the one this
          page generated (with an <code>external_id</code> column, polled live for its current status)
          or a results file straight from the client's own system (matched by email instead) — whenever
          you want to check. Repeat as often as needed.
        </p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '320px' }}>
            <label style={labelStyle}>Destination</label>
            <SearchableSelect value={fbDestId} onChange={setFbDestId}
              options={destOptionGroups.options} closedOptions={destOptionGroups.closedOptions}
              placeholder="— select destination —" />
          </div>
          <div>
            <label style={labelStyle}>Results CSV</label>
            <FileDrop file={fbFile} hint="Results CSV" onFile={(f) => setFbFile(f)} />
          </div>
          <button style={btnStyle('#d35400', !fbDestId || !fbFile || !fbFeedbackSupported || fbChecking)}
                  disabled={!fbDestId || !fbFile || !fbFeedbackSupported || fbChecking} onClick={checkFeedback}>
            {fbChecking ? 'Checking…' : '🔄 Check Feedback'}
          </button>
        </div>

        {fbDestId && !fbFeedbackSupported && (
          <div style={{ marginTop: '10px', color: '#7f8c8d', fontSize: '12px' }}>
            {schemaFor(fbDest.connector_type).label} delivers a final result at the moment each lead is
            sent — there's no separate feedback to check afterward for this destination.
          </div>
        )}

        {fbResult && (
          <div style={{ marginTop: '14px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: '#2c3e50' }}>
                {feedbackFromFile
                  ? <>Loaded <strong>{fbResult.results.length}</strong> row{fbResult.results.length !== 1 ? 's' : ''} from this file —
                      no <code>external_id</code> column was found, so no connector was polled; use the file's own status
                      column below instead.</>
                  : <><strong>{fbResult.found}</strong> of <strong>{fbResult.checked}</strong> leads found in the receiving system</>}
              </span>
              <button style={btnStyle('#8e44ad')} onClick={downloadFeedbackResults}>⬇ Download Feedback CSV</button>
            </div>

            <div style={{
              marginBottom: '14px', padding: '12px 14px', borderRadius: '6px',
              background: '#f4f6f7', border: '1px solid #d5dbdb',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50', marginBottom: '6px' }}>
                Update NeTRA lead status from these results
              </div>
              {!matchColumn ? (
                <div style={{ fontSize: '12px', color: '#555' }}>
                  Couldn't find a NeTRA Lead ID or Email column in this file, so results can't be matched
                  back to NeTRA leads automatically.
                  {Object.keys(fbResult.results[0]?.data || {}).length > 0 && (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span>Match leads by</span>
                      <select style={{ ...inputStyle, width: 'auto' }} value={matchMode}
                              onChange={(e) => setMatchMode(e.target.value)}>
                        <option value="">— select —</option>
                        <option value="lead_id">NeTRA Lead ID</option>
                        <option value="email">Email address</option>
                      </select>
                      <span>in column</span>
                      <select style={{ ...inputStyle, width: 'auto' }} value={matchColumn}
                              onChange={(e) => setMatchColumn(e.target.value)}>
                        <option value="">— select column —</option>
                        {Object.keys(fbResult.results[0].data).map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              ) : feedbackFromFile && !statusColumn ? (
                <div style={{ fontSize: '12px', color: '#555' }}>
                  Matching NeTRA leads by {MATCH_MODE_LABELS[matchMode] || matchMode} — the "<strong>{matchColumn}</strong>" column
                  (<span onClick={() => { setMatchMode(''); setMatchColumn(''); }} style={{ color: '#1a6da8', cursor: 'pointer', textDecoration: 'underline' }}>change</span>).
                  This file wasn't polled from a connector, so pick which column holds the client's own status/verdict:
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Status column</span>
                    <select style={{ ...inputStyle, width: 'auto' }} value={statusColumn}
                            onChange={(e) => setStatusColumn(e.target.value)}>
                      <option value="">— select column —</option>
                      {Object.keys(fbResult.results[0].data).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
                    Matching NeTRA leads by {MATCH_MODE_LABELS[matchMode] || matchMode} — the "<strong>{matchColumn}</strong>" column
                    (<span onClick={() => { setMatchMode(''); setMatchColumn(''); setStatusColumn(''); }} style={{ color: '#1a6da8', cursor: 'pointer', textDecoration: 'underline' }}>change</span>).
                    {feedbackFromFile && (
                      <> Status from the "<strong>{statusColumn}</strong>" column
                      (<span onClick={() => setStatusColumn('')} style={{ color: '#1a6da8', cursor: 'pointer', textDecoration: 'underline' }}>change</span>).</>
                    )}{' '}
                    Click a status below to mark it as meaning the client <strong>accepted</strong> or <strong>rejected</strong> the lead (click again to cycle, a third click clears it):
                  </div>
                  <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#555' }}>
                      Reason/QA flag column — stored on Rejected leads' QA Flags so the supplier can see why
                    </span>
                    <select style={{ ...inputStyle, width: 'auto' }} value={reasonColumn}
                            onChange={(e) => setReasonColumn(e.target.value)}>
                      <option value="">— use the status value itself —</option>
                      {Object.keys(fbResult.results[0]?.data || {}).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  {matchMode === 'email' && (
                    <div style={{ marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: '#555' }}>Match against leads in</span>
                      <SearchableSelect value={matchProgramId}
                              onChange={(v) => { setMatchProgramId(v); setMatchComponentId(''); setMatchGoalId(''); }}
                              options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
                              placeholder="— select program —" />
                      <select style={{ ...inputStyle, width: 'auto' }} value={matchComponentId}
                              onChange={(e) => { setMatchComponentId(e.target.value); setMatchGoalId(''); }}
                              disabled={!matchProgramId}>
                        <option value="">— all components —</option>
                        {components.filter((c) => String(c.program_id) === String(matchProgramId)).map((c) => (
                          <option key={c.id} value={c.id}>{c.component_name}</option>
                        ))}
                      </select>
                      <select style={{ ...inputStyle, width: 'auto' }} value={matchGoalId}
                              onChange={(e) => setMatchGoalId(e.target.value)}
                              disabled={!matchComponentId}>
                        <option value="">— all goals —</option>
                        {goals.filter((g) => String(g.component_id) === String(matchComponentId)).map((g) => (
                          <option key={g.id} value={g.id}>{g.goal_name}</option>
                        ))}
                      </select>
                      <span style={{ fontSize: '11px', color: '#888' }}>
                        (defaults to this destination's own scope — clear component/goal to search the whole program)
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {Array.from(new Set(fbResult.results.filter(isUsableRow).map(statusFor))).map((status) => {
                      const count = fbResult.results.filter((r) => isUsableRow(r) && statusFor(r) === status).length;
                      const decision = statusDecisions[status];
                      const pillStyle = decision === 'accepted'
                        ? { borderColor: '#27ae60', background: '#eafaf1', color: '#1e8449' }
                        : decision === 'rejected'
                        ? { borderColor: '#c0392b', background: '#fdedec', color: '#c0392b' }
                        : { borderColor: '#ccc', background: '#fafafa', color: '#555' };
                      return (
                        <button key={status ?? '__blank__'} type="button" onClick={() => cycleStatusDecision(status)} style={{
                          display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 10px',
                          borderRadius: '14px', cursor: 'pointer', fontSize: '12px', border: '1px solid',
                          userSelect: 'none', ...pillStyle,
                        }}>
                          {decision === 'accepted' ? '✓ ' : decision === 'rejected' ? '✗ ' : ''}
                          {status || '(blank)'} <span style={{ color: '#888' }}>({count})</span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button style={btnStyle('#27ae60', !clientAcceptedMatches.length || applyingStatus || (matchMode === 'email' && !matchProgramId))}
                            disabled={!clientAcceptedMatches.length || applyingStatus || (matchMode === 'email' && !matchProgramId)}
                            onClick={() => applyBulkStatus(clientAcceptedMatches, 'Client Accepted')}>
                      {applyingStatus ? 'Updating…'
                        : `✓ Mark ${clientAcceptedMatches.length} Lead${clientAcceptedMatches.length !== 1 ? 's' : ''} as Client Accepted`}
                    </button>
                    <button style={btnStyle('#c0392b', !clientRejectedMatches.length || applyingStatus || (matchMode === 'email' && !matchProgramId))}
                            disabled={!clientRejectedMatches.length || applyingStatus || (matchMode === 'email' && !matchProgramId)}
                            onClick={() => applyBulkStatus(clientRejectedMatches, 'Rejected')}>
                      {applyingStatus ? 'Updating…'
                        : `✗ Mark ${clientRejectedMatches.length} Lead${clientRejectedMatches.length !== 1 ? 's' : ''} as Rejected`}
                    </button>
                  </div>
                </>
              )}
              {applyResult && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: applyResult.status === 'Rejected' ? '#c0392b' : '#1e8449' }}>
                  ✓ Updated <strong>{applyResult.updated}</strong> lead{applyResult.updated !== 1 ? 's' : ''} to {applyResult.status}.
                  {applyResult.not_found.length > 0 && (
                    <div style={{ color: '#c0392b', marginTop: '4px' }}>
                      {applyResult.not_found.length} {matchMode === 'email' ? 'email address' : 'Lead ID'}
                      {applyResult.not_found.length !== 1 ? 's' : ''} didn't match any lead in NeTRA
                      {matchMode === 'email' ? ' within the selected program/component/goal' : ''}:{' '}
                      {applyResult.not_found.slice(0, 10).join(', ')}{applyResult.not_found.length > 10 ? '…' : ''}
                    </div>
                  )}
                  {(applyResult.not_found_elsewhere || []).length > 0 && (
                    <div style={{ color: '#b9770e', marginTop: '6px' }}>
                      {applyResult.not_found_elsewhere.length === 1 ? 'That lead exists' : 'Those leads exist'}
                      {' '}in NeTRA, just under a different scope than selected here:
                      <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                        {applyResult.not_found_elsewhere.map((m) => (
                          <li key={m.email}>
                            <strong>{m.email}</strong> — {m.program_name} › {m.component_name} › {m.goal_name}
                          </li>
                        ))}
                      </ul>
                      Re-run with a "Check Feedback" destination scoped to that program/component/goal if these should be updated.
                    </div>
                  )}
                </div>
              )}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={thStyle}>{feedbackFromFile ? (matchColumn || 'Match Key') : 'External ID'}</th>
                <th style={thStyle}>{feedbackFromFile ? 'Status (from file)' : 'Feedback Status'}</th>
                <th style={thStyle}>Found</th><th style={thStyle}>Detail</th>
              </tr></thead>
              <tbody>
                {fbResult.results.map((r, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>
                      {feedbackFromFile ? (matchColumn ? (r.data?.[matchColumn] || '—') : '—') : (r.external_id || '—')}
                    </td>
                    <td style={tdStyle}><StatusBadge status={statusFor(r)} /></td>
                    <td style={tdStyle}>{feedbackFromFile ? '—' : (r.found ? '✓' : '— not yet')}</td>
                    <td style={{ ...tdStyle, fontSize: '11px', color: '#666' }}>
                      {r.detail ? JSON.stringify(r.detail) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
